import { Controller } from '@hotwired/stimulus';
import { parseFormattedNumber } from '../utils/parse_formatted_number';

const FORMAT_PARTS_SAMPLE = 1234567.89;

export default class NumberFormatController extends Controller {
  static targets = ['input'];
  static values = {
    locale: String,
    scale: { type: Number, default: 0 },
  };

  connect() {
    this.localeValue = this.localeValue || 'hu-HU';

    this.formatter = new Intl.NumberFormat(this.localeValue, {
      useGrouping: true,
      minimumFractionDigits: 0,
      maximumFractionDigits: this.scaleValue,
    });

    this.decimalSeparator = this._detectDecimalSeparator(this.scaleValue);

    this._initRawValue();
  }

  onKeydown(e) {
    if (this._handleInvalidKey(e)) {
      return;
    }

    if (this._handleDecimalSeparatorKey(e)) {
      return;
    }

    this._handleBackspace(e);
  }

  _detectDecimalSeparator(scale) {
    if (scale === 0) {
      return null;
    }
    const parts = this.formatter.formatToParts(FORMAT_PARTS_SAMPLE);
    return parts.find((p) => p.type === 'decimal')?.value ?? null;
  }

  _initRawValue() {
    if (this.inputTarget.value) {
      const raw = parseFormattedNumber(this.inputTarget.value, this.decimalSeparator);
      this.inputTarget.dataset.rawValue = raw ?? '';
    }
  }

  _handleDecimalSeparatorKey(e) {
    const isDecimalSeparatorKey = this.decimalSeparator && e.key === this.decimalSeparator;

    if (!isDecimalSeparatorKey) {
      return false;
    }

    if (this.inputTarget.value.includes(this.decimalSeparator)) {
      e.preventDefault();
    }

    return true;
  }

  _handleBackspace(e) {
    if (e.key !== 'Backspace') {
      return;
    }

    const input = this.inputTarget;
    const pos = input.selectionStart;

    if (pos !== input.selectionEnd || pos < 2) {
      return;
    }

    const charBefore = input.value[pos - 1];
    if (/\d/.test(charBefore) || charBefore === this.decimalSeparator) {
      return;
    }

    e.preventDefault();
    this.skipOverSeparator(input, pos);
  }

  _handleInvalidKey(e) {
    const hasModifierKey = e.ctrlKey || e.metaKey;
    const isInvalidChar = e.key.length === 1 && !/\d/.test(e.key) && e.key !== this.decimalSeparator;

    if (!hasModifierKey && isInvalidChar) {
      e.preventDefault();
      return true;
    }

    return false;
  }

  skipOverSeparator(input, pos) {
    input.value = input.value.slice(0, pos - 2) + input.value.slice(pos - 1);
    input.setSelectionRange(pos - 2, pos - 2);
    this.onInput();
  }

  onInput() {
    const input = this.inputTarget;
    const oldValue = input.value;
    const oldPos = input.selectionStart;

    if (this.decimalSeparator && oldValue.endsWith(this.decimalSeparator)) {
      return;
    }

    const rawOldValue = parseFormattedNumber(oldValue, this.decimalSeparator);
    if (rawOldValue === null) {
      input.value = '';
      input.dataset.rawValue = '';
      return;
    }

    const newValue = this.formatter.format(+rawOldValue);
    input.value = newValue;
    input.dataset.rawValue = rawOldValue;

    const diff = newValue.length - oldValue.length;
    const newPos = Math.max(0, oldPos + diff);

    input.setSelectionRange(newPos, newPos);
  }
}
